import React from 'react';
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form';
import FieldError from './FieldError';
import DatePicker from '../../datePicker/DatePicker';
import { DatePickerProps } from '../../datePicker/DatePicker.interfaces';
import FieldLabel from './FieldLabel';
import { twMerge } from 'tailwind-merge';

type ControlledDatePickerProps<T extends FieldValues> = {
  name: Path<T>;
  label: string | React.ReactNode;
  className?: string;
  errorClassName?: string;
  isRequired?: boolean;
  disabled?: boolean;
  withoutTouch?: boolean;
} & Omit<DatePickerProps, 'value' | 'onChange' | 'hasError' | 'name'>;

const FormDatePicker = <T extends FieldValues>({
  name,
  label,
  isRequired,
  className,
  errorClassName,
  disabled,
  withoutTouch = true,
  ...props
}: ControlledDatePickerProps<T>) => {
  const { control } = useFormContext<T>();

  const {
    field,
    fieldState: { error, isTouched },
  } = useController({
    name,
    control,
  });

  const touch = withoutTouch || isTouched;

  return (
    <div className={twMerge(className, 'flex relative flex-col gap-0.5')}>
      <FieldLabel name={name} label={label} isRequired={isRequired} />
      <DatePicker
        {...props}
        isDisabled={disabled}
        id={name}
        name={field.name}
        value={field.value}
        onChange={(event) => {
          if (event.target.value === field.value) return;
          field.onChange(event.target.value);
        }}
        hasError={!!error && touch && !disabled}
        onBlur={field.onBlur}
      />
      {error && touch && !disabled && (
        <FieldError
          className={twMerge(
            'absolute top-[calc(100%_+_0.25rem)]',
            errorClassName,
          )}
        >
          {error.message}
        </FieldError>
      )}
    </div>
  );
};
export default FormDatePicker;
